import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  InputNumber,
  Select,
  Row,
  Col,
  Spin,
  Modal,
  message,
  Tabs,
  Input
} from 'antd';
import {
  DeleteOutlined,
  SwapOutlined,
  MenuOutlined
} from '@ant-design/icons';
import AxiosOICStat from 'AxiosOICStat';
import { useSelector } from 'react-redux';
import { getIndicatorName } from '../utils/indicatorName';

const { TabPane } = Tabs;
const { Search } = Input;
const { Option } = Select;

export default function SybContentOrder() {
  // — Redux state
  const indicatorsSlice  = useSelector(s => s.indicators);
  const indicatorsState   = indicatorsSlice.value || {};
  const indicatorsReady   = indicatorsSlice.status === 'succeeded';
  const rawCats         = useSelector(s => s.categories.value || {});
  const allCats         = Object.values(rawCats);

  // — Local state
  const [deckCats, setDeckCats] = useState([]);  
  const [poolCats, setPoolCats] = useState([]);  
  const [poolInds, setPoolInds] = useState([]);  
  const [loading, setLoading]   = useState(true);
  const [indFilter, setIndFilter] = useState('');

  // per‐row inputs
  const [catPos, setCatPos] = useState({});
  const [indCat, setIndCat] = useState({});

  // — Move‐Indicator Modal
  const [moveIndModalVisible, setMoveIndModalVisible] = useState(false);
  const [moveIndData, setMoveIndData]                 = useState({ catIdx: null, indIdx: null });
  const [moveIndDestCat, setMoveIndDestCat]           = useState(null);

  // — Move‐Category Modal
  const [moveCatModalVisible, setMoveCatModalVisible] = useState(false);
  const [moveCatIndex, setMoveCatIndex]               = useState(null);
  const [moveCatDestPos, setMoveCatDestPos]           = useState(1);

  // 1) Load current deck
  useEffect(() => {
    AxiosOICStat.get('/syb/order-data')
      .then(res =>{
        setDeckCats(res.data.orderData || res.data)
      })
      .catch(() => message.error('Failed to load deck'))
      .finally(() => setLoading(false));
  }, []);

  // 2) Load SYB indicators
  useEffect(() => {
    AxiosOICStat.get('/syb/syb_indicators/')
      .then(res => {
        setPoolInds(res.data.map(item => {
          const entry = indicatorsState[item.ind_code];
          return {
            indicatorCode: item.ind_code,
            indicatorName: entry ? getIndicatorName(entry) : item.ind_name_eng
          };
        }));
      })
      .catch(() => message.error('Failed to load SYB indicators'));
  }, [indicatorsState]);

  // 3) Rebuild category pool
  useEffect(() => {
    setPoolCats(
      allCats
        .filter(c => !deckCats.find(dc => dc.categoryCode === c.cat_code))
        .map(c => ({
          categoryCode: c.cat_code,
          categoryName: c.cat_name_eng,
          indicators: []
        }))
    );
  }, [allCats, deckCats]);

  // renumber helper
  const renumber = deck =>
    deck.map((cat, ci) => ({
      ...cat,
      order: `${ci+1}.`,
      indicators: cat.indicators.map((ind, ii) => ({
        ...ind,
        order: `${ci+1}.${ii+1}`
      }))
    }));

  // add from pools
  function addCategory(code) {
    const pos = catPos[code] || deckCats.length + 1;
    const cat = poolCats.find(c => c.categoryCode === code);
    const dc  = [...deckCats];
    dc.splice(pos - 1, 0, cat);
    setDeckCats(renumber(dc));
    setPoolCats(poolCats.filter(c => c.categoryCode !== code));
  }
  function addIndicator(code) {
    const target = indCat[code];
    if (!target) return message.error('Select a category');
    const dc  = [...deckCats];
    const ind = poolInds.find(i => i.indicatorCode === code);
    const ci  = dc.findIndex(c => c.categoryCode === target);
    dc[ci].indicators.push(ind); // always at end
    setDeckCats(renumber(dc));
    setPoolInds(poolInds.filter(i => i.indicatorCode !== code));
  }

  // category move/remove
  function moveCategoryUp(idx) {
    if (idx===0) return;
    const dc = [...deckCats];
    [dc[idx-1], dc[idx]] = [dc[idx], dc[idx-1]];
    setDeckCats(renumber(dc));
  }
  function moveCategoryDown(idx) {
    if (idx===deckCats.length-1) return;
    const dc = [...deckCats];
    [dc[idx], dc[idx+1]] = [dc[idx+1], dc[idx]];
    setDeckCats(renumber(dc));
  }
  function removeCategory(code) {
    const dc = [...deckCats];
    const i  = dc.findIndex(c => c.categoryCode===code);
    const [rem] = dc.splice(i,1);
    setDeckCats(renumber(dc));
    setPoolCats([...poolCats, { 
      categoryCode: rem.categoryCode, 
      categoryName: rem.categoryName, 
      indicators: [] 
    }]);
    setPoolInds([...poolInds, ...rem.indicators]);
  }
  function moveCategoryToPosition() {
    const from = moveCatIndex;
    const to   = moveCatDestPos-1;
    const dc   = [...deckCats];
    const [mv] = dc.splice(from,1);
    dc.splice(to,0,mv);
    setDeckCats(renumber(dc));
    setMoveCatModalVisible(false);
  }

  const filteredPoolInds = poolInds.filter(ind => {
    if (!ind.indicatorName && !ind.indicatorCode) return false;
    const needle = (indFilter || '').toLowerCase();
    if (!needle) return true;
    const nameMatch = ind.indicatorName && ind.indicatorName.toLowerCase().includes(needle);
    const codeMatch = String(ind.indicatorCode).toLowerCase().includes(needle);
    return nameMatch || codeMatch;
  });

  if (loading) return <Spin tip="Loading…" style={{ margin: 50 }} />;

  // indicator move/remove
  function moveIndUp(cIdx,iIdx) {
    if (iIdx===0) return;
    const dc = [...deckCats];
    const arr = [...dc[cIdx].indicators];
    [arr[iIdx-1], arr[iIdx]] = [arr[iIdx], arr[iIdx-1]];
    dc[cIdx].indicators = arr;
    setDeckCats(renumber(dc));
  }
  function moveIndDown(cIdx,iIdx) {
    const dc = [...deckCats];
    const arr = [...dc[cIdx].indicators];
    if(iIdx===arr.length-1) return;
    [arr[iIdx], arr[iIdx+1]] = [arr[iIdx+1], arr[iIdx]];
    dc[cIdx].indicators = arr;
    setDeckCats(renumber(dc));
  }
  function removeIndicator(catCode, indCode) {
    const dc = [...deckCats];
    const ci = dc.findIndex(c=>c.categoryCode===catCode);
    const rem = dc[ci].indicators.find(i=>i.indicatorCode===indCode);
    dc[ci].indicators = dc[ci].indicators.filter(i=>i.indicatorCode!==indCode);
    setDeckCats(renumber(dc));
    setPoolInds([...poolInds, rem]);
  }
  function moveIndicatorToCategory() {
    const { fromCatCode, indicatorCode } = moveIndData;
    const dc = deckCats.map(cat => ({ ...cat })); // shallow clone
  
    // 1) remove from source category
    const source = dc.find(c => c.categoryCode === fromCatCode);
    const moving = source.indicators.find(i => i.indicatorCode === indicatorCode);
    source.indicators = source.indicators.filter(i => i.indicatorCode !== indicatorCode);
  
    // 2) add to target category (always push to end)
    const dest = dc.find(c => c.categoryCode === moveIndDestCat);
    dest.indicators = [...dest.indicators, moving];
  
    // 3) renumber & close modal
    setDeckCats(renumber(dc));
    setMoveIndModalVisible(false);
  }

  // --- Drag & drop handlers
  function handleCatDragStart(e, index) {
    e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'category', index }));
    e.dataTransfer.effectAllowed = 'move';
  }
  function handleCatDrop(e, index) {
    e.preventDefault();
    try {
      const payload = JSON.parse(e.dataTransfer.getData('text/plain'));
      if (payload.type !== 'category') return;
      const from = payload.index;
      const rect = e.currentTarget.getBoundingClientRect();
      const mid = rect.top + rect.height / 2;
      let to = (e.clientY > mid) ? index + 1 : index;
      if (from === to || from + 1 === to) return; // no-op (dropped in same spot)
      const dc = [...deckCats];
      const [mv] = dc.splice(from, 1);
      if (from < to) to = to - 1;
      dc.splice(to, 0, mv);
      setDeckCats(renumber(dc));
    } catch (err) {
      // ignore
    }
  }

  function handleIndDragStart(e, fromCatIdx, fromIndIdx) {
    e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'indicator', fromCatIdx, fromIndIdx }));
    e.dataTransfer.effectAllowed = 'move';
  }
  function handleIndDrop(e, toCatIdx, toIndIdx) {
    e.preventDefault();
    try {
      const payload = JSON.parse(e.dataTransfer.getData('text/plain'));
      if (payload.type !== 'indicator') return;
      const { fromCatIdx, fromIndIdx } = payload;
      const dc = deckCats.map(cat => ({ ...cat, indicators: [...cat.indicators] }));
      const source = dc[fromCatIdx];
      const moving = source.indicators.splice(fromIndIdx, 1)[0];
      const rect = e.currentTarget.getBoundingClientRect();
      const mid = rect.top + rect.height / 2;
      let toIdx = (e.clientY > mid) ? toIndIdx + 1 : toIndIdx;
      if (fromCatIdx === toCatIdx && fromIndIdx < toIdx) toIdx = toIdx - 1;
      const dest = dc[toCatIdx];
      if (toIdx < 0) toIdx = 0;
      if (toIdx > dest.indicators.length) toIdx = dest.indicators.length;
      dest.indicators.splice(toIdx, 0, moving);
      setDeckCats(renumber(dc));
    } catch (err) {
      // ignore
    }
  }
  
  

  function saveOrder() {
    AxiosOICStat.post('/syb/order-data',{orderData:deckCats})
      .then(()=>message.success('Order saved'))
      .catch(()=>message.error('Save failed'));
  }

  if(loading) return <Spin tip="Loading…" style={{margin:50}}/>;

  // Deck columns
  const deckColumns = [
    { title:'Order', dataIndex:'order', width:80 },
    { title:'Category', dataIndex: 'categoryName' },
    {
      title:'Actions',
      key:'actions',
      render:(_,rec,idx)=>(
        <>
          <Button icon={<MenuOutlined/>} size="small" style={{ cursor: 'grab', marginRight: 8 }} />
          <Button icon={<DeleteOutlined/>} size="small"
            onClick={()=>removeCategory(rec.categoryCode)}
            style={{marginLeft:8}}/>
        </>
      )
    }
  ];

  return (
    <>
      <Row gutter={16} style={{ padding: 20 }}>
        {/* Current Order */}
        <Col span={12}>
          <h3>Current Order</h3>
          <Table
            dataSource={deckCats}
            columns={deckColumns}
            rowKey="categoryCode"
            pagination={false}
            onRow={(record, index) => ({
              draggable: true,
              onDragStart: e => handleCatDragStart(e, index),
              onDragOver: e => e.preventDefault(),
              onDrop: e => handleCatDrop(e, index)
            })}
            expandable={{
              expandedRowRender: (cat, categoryIndex) => (
                <Table
                  dataSource={cat.indicators}
                  rowKey="indicatorCode"
                  pagination={false}
                  size="small"
                  onRow={(record, indicatorIndex) => ({
                    draggable: true,
                    onDragStart: e => handleIndDragStart(e, categoryIndex, indicatorIndex),
                    onDragOver: e => e.preventDefault(),
                    onDrop: e => handleIndDrop(e, categoryIndex, indicatorIndex)
                  })}
                  columns={[
                    { title: 'Order', dataIndex: 'order', width: 100 },
                    { title: 'Indicator', dataIndex: 'indicatorName' },
                    {
                      title: 'Actions',
                      key: 'indActions',
                      render: (_, ind, indicatorIndex) => (
                        <>
                          <Button icon={<MenuOutlined/>} size="small" style={{ cursor: 'grab', marginRight:8 }} />
                          <Button
                            icon={<SwapOutlined />}
                            size="small"
                            onClick={() => {
                              setMoveIndData({
                                fromCatCode: cat.categoryCode,
                                indicatorCode: ind.indicatorCode
                              });
                              setMoveIndDestCat(cat.categoryCode);
                              setMoveIndModalVisible(true);
                            }}
                          />
                          <Button
                            icon={<DeleteOutlined />}
                            size="small"
                            onClick={() =>
                              removeIndicator(cat.categoryCode, ind.indicatorCode)
                            }
                            style={{ marginLeft: 8 }}
                          />
                        </>
                      )
                    }
                  ]}
                />
              )
            }}
          />
          <Button type="primary" onClick={saveOrder} style={{ marginTop: 16 }}>
            Save Order
          </Button>
        </Col>

        {/* Pools */}
        <Col span={12}>
          <Tabs defaultActiveKey="1" type="card">
            <TabPane tab="Category Pool" key="1">
              <Table
                dataSource={poolCats}
                rowKey="categoryCode"
                size="small"
                pagination={false}
                columns={[
                  { title: 'Category', dataIndex: 'categoryName' },
                  {
                    title: 'Position',
                    render: (_, r) => (
                      <InputNumber
                        min={1}
                        style={{ width: 80 }}
                        value={catPos[r.categoryCode]}
                        onChange={(val) =>
                          setCatPos({ ...catPos, [r.categoryCode]: val })
                        }
                      />
                    )
                  },
                  {
                    title: 'Action',
                    render: (_, r) => (
                      <Button onClick={() => addCategory(r.categoryCode)}>
                        Add
                      </Button>
                    )
                  }
                ]}
              />
            </TabPane>

            <TabPane tab="Indicator Pool" key="2">
              
            <Search
              placeholder="Search indicators (name or code)"
              allowClear
              onChange={e => setIndFilter(e.target.value)}
              style={{ width: '100%', marginBottom: 12 }}
            />
              <Table
                dataSource={indicatorsReady ? filteredPoolInds : []}
                loading={!indicatorsReady}
                rowKey="indicatorCode"
                size="small"
                pagination={{pageSize:"10"}}
                columns={[
                  { title: 'Code', dataIndex: 'indicatorCode', width: 120 },
                  { title: 'Indicator', dataIndex: 'indicatorName' },
                  {
                    title: 'Category',
                    render: (_, r) => (
                      <Select
                        style={{ width: 160 }}
                        placeholder="Choose category"
                        value={indCat[r.indicatorCode]}
                        onChange={(val) =>
                          setIndCat({ ...indCat, [r.indicatorCode]: val })
                        }
                      >
                        {deckCats.map((c) => (
                          <Option key={c.categoryCode} value={c.categoryCode}>
                            {c.categoryName}
                          </Option>
                        ))}
                      </Select>
                    )
                  },
                  {
                    title: 'Action',
                    render: (_, r) => (
                      <Button onClick={() => addIndicator(r.indicatorCode)}>
                        Add
                      </Button>
                    )
                  }
                ]}
              />
            </TabPane>
          </Tabs>
        </Col>
      </Row>

      {/* Move-Category Modal */}
      <Modal
        title="Move Category"
        visible={moveCatModalVisible}
        onCancel={() => setMoveCatModalVisible(false)}
        onOk={moveCategoryToPosition}
      >
        <InputNumber
          min={1}
          max={deckCats.length}
          style={{ width: '100%' }}
          value={moveCatDestPos}
          onChange={(val) => setMoveCatDestPos(val)}
          placeholder="New position"
        />
      </Modal>

      {/* Move-Indicator Modal */}
      <Modal
        title="Move Indicator"
        visible={moveIndModalVisible}
        onCancel={() => setMoveIndModalVisible(false)}
        onOk={moveIndicatorToCategory}
      >
        <Select
          style={{ width: '100%' }}
          placeholder="Target category"
          value={moveIndDestCat}
          onChange={(val) => setMoveIndDestCat(val)}
        >
          {deckCats.map((c) => (
            <Option key={c.categoryCode} value={c.categoryCode}>
              {c.categoryName}
            </Option>
          ))}
        </Select>
      </Modal>
    </>
  );
  
}
